import React, { FC, useState, useEffect } from "react";

// UI
import { Box } from "@UI/Components/Box/Box";
import { Stack } from "@UI/Components/Stack/Stack";

// Component

// Messages
import messages from "./messages";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// hooks
import { ResponsiveFixedButton } from "@UI/Components/ResponsiveFixedButton/ResponsiveFixedButton";
import { StripeCreditCardForm } from "../../../Adapters/Stripe/StripeCreditCardForm";
import { usePaymentIntent } from "@modules/PaymentProcessing/modules/CreditCard/hooks/stripe/usePaymentIntent";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { Money } from "@Shared/Components/Money/Money";
import { withStripe } from "@modules/PaymentProcessing/providers/stripe/hocs/withStripe";
import { CreditCardPreview } from "@modules/PaymentProcessing/modules/CreditCard/Components/CreditCardPreview/CreditCardPreview";
import { useUserPaymentMethods } from "@modules/PaymentProcessing/hooks/useUserPaymentMethods";

interface Props {
  onSubmit?: (arg: string) => void;
  onSetupError?: () => void;
  setIsServerError?: (arg: boolean) => void;
  children?: React.ReactNode;
}

export const PayWithCreditCard: FC<Props> = withStripe(
  ({ onSubmit, setIsServerError, onSetupError, children }) => {
    const { getPreferredCreditCard, isSuccess: paymentMethodsFetched } =
      useUserPaymentMethods();
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSuccessfulSetup = () => {
      setIsSuccess(true);
      onSubmit("stripe");
    };

    const [preferredCreditCard, setPreferredCreditCard] = useState(undefined);

    useEffect(() => {
      setPreferredCreditCard(getPreferredCreditCard());
    }, [paymentMethodsFetched]);

    const [name, setName] = useState("");
    const [formCompleted, setFormCompleted] = useState(false);
    const {
      submit,
      stripeError,
      cleanError,
      setStripeError,
      isLoading,
      setupConfig,
      isSetupError,
    } = usePaymentIntent({
      paymentMethodId: preferredCreditCard?.details?.paymentMethodId,
      name,
      onSuccess: handleSuccessfulSetup,
      onError: () => setIsServerError(true),
      onSetupError,
      enabled: !isSuccess,
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      submit.mutate();
    };

    const handleNameChange = (name: string) => {
      setName(name);
      cleanError();
    };

    const handleCompleted = () => {
      setFormCompleted(true);
    };

    if (isLoading || !paymentMethodsFetched || isSetupError || isSuccess) {
      return <LoadingScreen />;
    }

    const submitDisabled =
      !preferredCreditCard &&
      (!formCompleted || !!stripeError || submit.isLoading);

    return (
      <Stack spacing={5}>
        {preferredCreditCard ? (
          <CreditCardPreview
            onChangeCard={() => setPreferredCreditCard(undefined)}
            paymentMethod={preferredCreditCard}
            stripeError={stripeError}
          />
        ) : (
          <StripeCreditCardForm
            onNameChange={handleNameChange}
            setStripeError={setStripeError}
            stripeError={stripeError}
            cleanError={cleanError}
            onCompleted={handleCompleted}
          />
        )}

        {children}
        <Box>
          <ResponsiveFixedButton
            data-testid="submit-credit-card-button"
            isLoading={submit.isLoading}
            h={12}
            width="full"
            colorScheme="seaGreen"
            onClick={handleSubmit}
            isDisabled={submitDisabled}
          >
            <FormattedMessage
              {...messages.pay}
              values={{
                amount: (
                  <Money
                    cents={setupConfig.amountCents}
                    currency={setupConfig.currency}
                  />
                ),
              }}
            />
          </ResponsiveFixedButton>
        </Box>
      </Stack>
    );
  }
);
